import type { Ownership } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import dayjs from 'dayjs'
import { useRouter } from 'expo-router'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { View } from 'react-native'
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import { type Action, ActionButton } from '~/components/Actions'
import AppActions from '~/components/AppActions'
import { LeadFeedback } from '~/components/feedback/LeadFeedback'
import { useAlert } from '~/components/GlobalAlert'
import Icon from '~/components/Icon'
import ContentBox, { BoxActionsHeader } from '~/components/layout/ContentBox'
import { useScrollContext } from '~/components/layout/ScrollContext'
import { ViewTitle } from '~/components/layout/Sections'
import { ValueList, type ValueItem } from '~/components/layout/ValueList'
import { InactiveBadge, LeadVerificationBadge } from '~/components/leads/LeadBadges'
import LeadContactRequestSheet from '~/components/leads/LeadContactRequestSheet'
import { LeadInterests } from '~/components/leads/LeadInterests'
import { LeadMatchingAssets } from '~/components/leads/LeadMatchingAssets'
import { LeadReadinessIntelligence } from '~/components/leads/LeadReadinessIntelligence'
import { useLeadStore } from '~/components/leads/leadStore'
import {
  getLeadState,
  type LeadDetail,
  type LeadDetailTabs,
  type LeadState,
} from '~/components/leads/types'
import { getLeadName } from '~/components/leads/utils'
import { LoadingView } from '~/components/LoadingView'
import { EditNotesSheet } from '~/components/notes/EditNotesSheet'
import { NotesDisplay } from '~/components/notes/NotesDisplay'
import RealtorCard from '~/components/realtors/RealtorCard'
import { ScrollView } from '~/components/ScrollView'
import { Body, DisplayValue, Label } from '~/components/typography/Content'
import { Heading1 } from '~/components/typography/Titles'
import { TabBar, TabContent, Tabs, type TabItem } from '~/components/ui/tabs'
import { LeadTypeBadge } from '~/components/ui/TypeBadge'
import { useLeadOwnerFromRequest, useUpdateContactNotes } from '~/data/contacts/useContacts'
import { useHasFeedContent } from '~/data/feed/useFeed'
import { useDeleteLead, useLead, useLeadContactRequestStatus } from '~/data/leads/useLeads'
import { queryClient } from '~/lib/trpc'
import { cn } from '~/lib/utils'
import { usePublicPreviewAuth } from '../auth/PublicPreviewProvider'
import { LinkButton } from '../ui/button'

export function LeadDetailView({
  leadId,
  initialView,
}: {
  leadId: string
  initialView?: LeadDetailTabs
}) {
  const { t } = useTranslations('leads')
  const { notifyLeadDataReady } = usePublicPreviewAuth()

  const { data: lead, isLoading, isAuthenticatedDataReady } = useLead(leadId)
  const leadState = lead ? getLeadState(lead) : null

  useEffect(() => {
    if (isAuthenticatedDataReady) {
      notifyLeadDataReady()
    }
  }, [isAuthenticatedDataReady, notifyLeadDataReady])

  return isLoading ? (
    <ContentBox contentContainerClassName="items-center justify-center">
      <LoadingView />
    </ContentBox>
  ) : !lead ? (
    <ContentBox contentContainerClassName="items-center justify-center py-8">
      <Body>{t(($) => $.detail.notFound)}</Body>
    </ContentBox>
  ) : leadState === 'own' || leadState === 'foreign-purchased' ? (
    <OwnedLeadContent
      lead={lead}
      isInactive={lead.disabled_reason === 'INACTIVE'}
      leadState={leadState}
      initialView={initialView}
    />
  ) : (
    <ForeignAvailableLeadContent
      lead={lead}
      isInactive={lead.disabled_reason === 'INACTIVE'}
      initialView={initialView}
    />
  )
}

function useLeadTabs({
  lead,
  onOfferAsset,
  initialView,
}: {
  lead: LeadDetail
  onOfferAsset?: (assetId: string) => void
  initialView?: LeadDetailTabs
}) {
  const { t } = useTranslations('leads')
  const { isInPublicPreview } = usePublicPreviewAuth()

  const isInactiveLead = lead.disabled_reason === 'INACTIVE'
  const [activeTab, setActiveTab] = useState<LeadDetailTabs>(
    isInactiveLead ? 'feedback' : (initialView ?? 'interest')
  )

  const ownedContactId = lead.is_own || lead.is_purchased ? lead.ownedContactId : undefined
  const hasInterests = lead.interests.length > 0

  const tabItems = useMemo(() => {
    const formattedDate = lead.interests[0]
      ? dayjs(lead.interests[0].created_at).format('D.M.YYYY')
      : undefined

    const tabItems: (TabItem<LeadDetailTabs> | false)[] = [
      ...(hasInterests && !isInactiveLead
        ? ([
            {
              id: 'interest',
              label: t(($) => $.detail.tabs.interest),
              animationOrigin: 'top-left',
              count: lead.interests.length ? 1 : undefined,
              content: (
                <ContentBox
                  scrollable={false}
                  contentContainerClassName="gap-2 px-0 py-6"
                  className={cn(!isInPublicPreview && 'rounded-tl-none')}>
                  <ViewTitle className="text-center">
                    {t(($) => $.detail.tabs.profileTitle)}
                    {lead.is_verified && <LeadVerificationBadge label={false} />}
                  </ViewTitle>
                  <View className="flex-row items-center justify-center gap-1">
                    <Label className="text-sm text-secondary">
                      {t(($) => $.detail.tabs.profileCreated)}
                    </Label>
                    <DisplayValue className="text-sm text-secondary">{formattedDate}</DisplayValue>
                  </View>
                  <LeadInterests lead={lead} />
                </ContentBox>
              ),
            },
            !isInPublicPreview && {
              id: 'matches',
              label: t(($) => $.detail.matchingAssets.title),
              animationOrigin: 'top-center',
              content: (
                <ContentBox
                  scrollable={false}
                  contentContainerClassName="gap-4 px-0 py-6"
                  className={cn(!ownedContactId && 'rounded-tr-none')}>
                  <ViewTitle className="text-center">
                    {t(($) => $.detail.matchingAssets.title)}
                  </ViewTitle>
                  <LeadMatchingAssets leadId={lead.id} onOfferAsset={onOfferAsset} />
                </ContentBox>
              ),
            },
          ].filter(Boolean) as TabItem<LeadDetailTabs>[])
        : []),
      !!ownedContactId && {
        id: 'feedback',
        label: t(($) => $.detail.tabs.feedback),
        animationOrigin: isInactiveLead ? 'top-left' : 'top-right',
        content: (
          <ContentBox
            scrollable={false}
            contentContainerClassName="gap-4 px-0 pb-6 pt-6"
            className={cn(!isInactiveLead && hasInterests && 'rounded-tr-none')}>
            <ViewTitle className="px-6 text-center">
              {t(($) => $.detail.tabs.feedbackTitle)}
            </ViewTitle>
            <View className="px-4">
              <LeadFeedback contactIds={lead.contactIds} />
            </View>
            {!isInactiveLead && (
              <AppActions
                actions={[
                  {
                    key: 'request-feedback',
                    label: t(($) => $.list.newFeedbackRequest),
                    icon: 'comment-plus-outline',
                    iconLibrary: 'materialcommunityicons',
                    href: `/feedback-request/add?contactId=${ownedContactId}`,
                    buttonVariant: 'primary',
                    buttonSize: 'lg',
                    description: t(($) => $.list.requestFeedbackDescription),
                  },
                ]}
              />
            )}
          </ContentBox>
        ),
      },
    ]

    return tabItems.filter(Boolean) as TabItem<LeadDetailTabs>[]
  }, [lead, ownedContactId, hasInterests, isInactiveLead, onOfferAsset, t])

  return {
    tabs: tabItems,
    activeTab,
    setActiveTab,
  }
}

function LeadTabsContent({ tabs }: { tabs: TabItem<LeadDetailTabs>[] }) {
  return tabs.length > 1 ? (
    <View className="flex-1">
      <TabBar className="gap-1.5 border-0 bg-transparent" />
      <TabContent />
    </View>
  ) : (
    (tabs[0]?.content as ReactNode)
  )
}

function OwnedLeadContent({
  lead,
  leadState,
  isInactive,
  initialView,
}: {
  lead: LeadDetail
  leadState: LeadState
  isInactive: boolean
  initialView?: LeadDetailTabs
}) {
  const { tabs, activeTab, setActiveTab } = useLeadTabs({ lead, initialView })

  return (
    <Tabs<LeadDetailTabs> tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab}>
      <ScrollView
        className={cn(
          'flex-1 rounded-content transition-opacity duration-150',
          isInactive && 'pointer-events-none opacity-50'
        )}
        offsetTrackHeight
        contentContainerClassName="gap-3">
        <LeadContactInfoBox lead={lead} leadState={leadState} />
        <LeadTabsContent tabs={tabs} />
      </ScrollView>
    </Tabs>
  )
}

function getLeadOwnership(lead: LeadDetail): Ownership {
  if (lead.is_own) {
    return 'own'
  }

  if (lead.is_purchased) {
    // eslint-disable-next-line i18next/no-literal-string
    return 'purchased'
  }

  if (lead.organisation) {
    return 'organisation'
  }

  // eslint-disable-next-line i18next/no-literal-string
  return 'network'
}

function LeadHeader({ lead, leadState }: { lead: LeadDetail; leadState?: LeadState }) {
  const { t } = useTranslations('leads')
  const redactedName = lead.is_verified
    ? t(($) => $.verification.verifiedContact)
    : t(($) => $.verification.newContact)

  const ownedContactId = lead.ownedContactId
  const leadName = getLeadName(lead.name ?? '') || redactedName
  const hasAccess = lead.is_own || lead.is_purchased
  const ownership = getLeadOwnership(lead)
  const isInactiveLead = lead.disabled_reason === 'INACTIVE'

  const { showAlert } = useAlert()
  const router = useRouter()
  const { mutate: deleteLead, isPending: isDeletingLead } = useDeleteLead()

  const handleDeleteLead = () => {
    showAlert({
      type: 'confirm',
      title: t(($) => $.detail.actions.deleteLeadConfirmTitle),
      description: t(($) => $.detail.actions.deleteLeadConfirmDescription),
      actions: [
        {
          key: 'confirm-delete',
          label: t(($) => $.detail.actions.deleteLeadConfirmButton),
          buttonVariant: 'destructive',
          buttonSize: 'lg',
          onPress: ({ closeAlert }) => {
            deleteLead(
              { leadId: lead.id },
              {
                onSuccess: () => {
                  closeAlert()
                  router.replace('/')
                },
                onError: () => {
                  showAlert({
                    type: 'error',
                    title: t(($) => $.errors.title),
                    description: t(($) => $.errors.deleteFailed),
                  })
                },
              }
            )
          },
        },
      ],
      cancelButton: true,
    })
  }

  return (
    <BoxActionsHeader
      actions={
        leadState === 'own'
          ? ([
              !!ownedContactId &&
                isInactiveLead && {
                  key: 'delete-lead',
                  label: t(($) => $.detail.actions.deleteLead),
                  icon: 'trash-outline',
                  onPress: handleDeleteLead,
                  disabled: isDeletingLead,
                  theme: '#fef2f2',
                },
              hasAccess &&
                !isInactiveLead && {
                  key: 'edit-contact',
                  buttonSize: 'sm',
                  label: t(($) => $.detail.actions.editContact),
                  icon: 'account-edit-outline',
                  href: {
                    pathname: '/(authenticated)/leads/[leadId]/edit',
                    params: { leadId: lead.id },
                  },
                },
            ].filter(Boolean) as Action[])
          : []
      }>
      <View className="items-center gap-3">
        <Heading1 className="text-center text-3xl">{leadName}</Heading1>
        <View className="flex-row items-center gap-2">
          {isInactiveLead ? (
            <InactiveBadge />
          ) : lead.is_verified ? (
            <LeadVerificationBadge />
          ) : (
            <Icon name="warning-outline" size={16} className="text-destructive" />
          )}
          {!isInactiveLead && (
            <LeadTypeBadge
              ownership={ownership}
              organisationName={lead.organisation?.name}
              realtorName={lead.organisation?.realtorName}
            />
          )}
        </View>
      </View>
    </BoxActionsHeader>
  )
}

function LeadContactInfoBox({ lead, leadState }: { lead: LeadDetail; leadState: LeadState }) {
  const { t } = useTranslations('leads')
  const isInactiveLead = lead.disabled_reason === 'INACTIVE'

  const contactValues = useMemo(() => {
    const values: ValueItem[] = []

    if (lead.phone) {
      values.push({
        icon: 'call-outline',
        value: t(($) => $.detail.contactInfo.phone),
        description: lead.phone,
      })
    }

    if (lead.email) {
      values.push({
        icon: 'mail-outline',
        value: t(($) => $.detail.contactInfo.email),
        description: lead.email,
      })
    }

    return values
  }, [lead.email, lead.phone, t])

  const ownedContactId = lead.ownedContactId
  const canEditNotes = leadState === 'own' && !!ownedContactId && !isInactiveLead
  const notes = lead.privateData?.notes
  const { mutate: updateNotes, isPending: isUpdatingNotes } = useUpdateContactNotes()

  const purchasedContactId = lead.is_purchased ? ownedContactId : undefined
  const { data: commissionOwnerId } = useLeadOwnerFromRequest(purchasedContactId)

  const handleSaveNotes = (newNotes: string) => {
    if (!ownedContactId) {
      return
    }

    updateNotes(
      { contactId: ownedContactId, notes: newNotes },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: ['leads', lead.id],
          })
        },
      }
    )
  }

  return (
    <ContentBox
      contentContainerClassName="gap-4 p-0"
      scrollable={false}
      header={<LeadHeader lead={lead} leadState={leadState} />}>
      <View className="gap-6 p-6">
        {isInactiveLead && (
          <View className="flex-row items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3">
            <Icon name="ban" size={18} className="text-destructive" />
            <Body className="flex-1 text-sm text-destructive">
              {t(($) => $.detail.status.inactiveNotice)}
            </Body>
          </View>
        )}
        {lead.disabled_reason === 'RESERVED' && (
          <View className="flex-row items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
            <Icon name="lock-closed-outline" size={18} className="text-amber-600" />
            <Body className="flex-1 text-sm text-amber-700">
              {t(($) => $.detail.status.reservedNotice)}
            </Body>
          </View>
        )}
        <View className="w-full overflow-hidden rounded-xl border border-border">
          <ValueList values={contactValues} />
        </View>
        {!isInactiveLead && (
          <LeadReadinessIntelligence readiness={lead.feedbackIntelligence?.readiness ?? null} />
        )}
        {!isInactiveLead && lead.is_purchased && commissionOwnerId && (
          <View className="gap-3">
            <ViewTitle className="text-base">{t(($) => $.myLeads.commissionContract)}</ViewTitle>
            <View className="rounded-xl border border-border bg-success/5 p-4">
              <RealtorCard userId={commissionOwnerId} showContactInfo />
            </View>
          </View>
        )}
        {canEditNotes && (
          <NotesDisplay
            notes={notes}
            canEdit={canEditNotes}
            className="mx-0"
            editButton={
              <EditNotesSheet
                modalKey={`edit-contact-notes-${ownedContactId}`}
                currentNotes={notes ?? undefined}
                onSave={handleSaveNotes}
                isPending={isUpdatingNotes}
                triggerVariant="link"
                triggerSize="sm"
                triggerLabel={t(($) => $.detail.actions.editNotes)}
              />
            }
          />
        )}
      </View>
      {!!ownedContactId && !isInactiveLead && (
        <AppActions
          actions={[
            {
              key: 'request-feedback',
              label: t(($) => $.detail.actions.requestFeedback),
              description: t(($) => $.list.requestFeedbackDescription),
              icon: 'comment-plus-outline',
              iconLibrary: 'materialcommunityicons',
              href: `/feedback-request/add?contactId=${ownedContactId}`,
              theme: '#D5FBCF',
            },
          ]}
        />
      )}
    </ContentBox>
  )
}

type LeadContentProps = { lead: LeadDetail }

function ContactRequestPendingStatus() {
  const { t } = useTranslations('leads')

  return (
    <View className="items-center gap-2 rounded-xl border border-secondary/30 bg-secondary/10 px-6 py-4">
      <View className="flex-row items-center gap-2">
        <Icon name="checkmark-circle" size={20} className="text-secondary" />
        <Body className="font-medium text-secondary">
          {t(($) => $.requestContact.statusPending)}
        </Body>
      </View>
      <Label className="text-center text-sm text-muted-foreground">
        {t(($) => $.requestContact.statusPendingDescription)}
      </Label>
    </View>
  )
}

function ForeignAvailableLeadContent({
  lead,
  isInactive,
  initialView,
}: LeadContentProps & { isInactive: boolean; initialView?: LeadDetailTabs }) {
  const { t } = useTranslations(['leads', 'common'])
  const { setOfferAssetId } = useLeadStore()

  const { openAuthModal, isInPublicPreview } = usePublicPreviewAuth()
  const { hasFeedContent, isLoadingFeedContent } = useHasFeedContent(!isInPublicPreview)

  const [isContactRequestSheetOpen, setIsContactRequestSheetOpen] = useState(false)

  const { data: contactRequestStatus } = useLeadContactRequestStatus(
    !isInPublicPreview ? lead.id : ''
  )

  const hasPendingRequest = contactRequestStatus?.status === 'pending'
  const hasAcceptedRequest = contactRequestStatus?.status === 'accepted'
  const rejectedAssetIds = contactRequestStatus?.rejectedAssetIds ?? []

  const handleOfferAsset = (assetId: string) => {
    setOfferAssetId(assetId)
    setIsContactRequestSheetOpen(true)
  }

  const { tabs, activeTab, setActiveTab } = useLeadTabs({
    lead,
    onOfferAsset: hasPendingRequest || hasAcceptedRequest ? undefined : handleOfferAsset,
    initialView,
  })

  const { scrollY } = useScrollContext()
  const inlineButtonBottomY = useSharedValue(0)

  const teaserValues: ValueItem[] = useMemo(
    () => [
      {
        icon: 'call-outline',
        value: t(($) => $.detail.teaser.phone),
        description: t(($) => $.detail.teaser.phoneValue),
      },
      {
        icon: 'mail-outline',
        value: t(($) => $.detail.teaser.email),
        description: t(($) => $.detail.teaser.emailValue),
      },
    ],
    [t]
  )

  const footerAnimatedStyle = useAnimatedStyle(() => {
    const isInlineButtonVisible = scrollY.value < inlineButtonBottomY.value - 50
    const translateY = withTiming(isInlineButtonVisible ? 80 : 0, {
      duration: 150,
    })

    return { transform: [{ translateY }] }
  })

  const requestContactButton = useMemo(
    () => (
      <ActionButton
        action={{
          key: 'request-contact',
          label: t(($) => $.requestContact.requestButton),
          icon: 'chatbubbles-outline',
          buttonVariant: 'primary',
          buttonSize: 'lg',
          onPress: () => {
            if (isInPublicPreview) {
              openAuthModal()
            } else {
              setIsContactRequestSheetOpen(true)
            }
          },
        }}
        className="self-center"
      />
    ),
    [t, isInPublicPreview, openAuthModal]
  )

  const showRequestButton =
    isInPublicPreview || (!hasPendingRequest && !hasAcceptedRequest && !lead.disabled_at)

  return (
    <Tabs<LeadDetailTabs> tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab}>
      <LeadContactRequestSheet
        leadId={lead.id}
        isOpen={isContactRequestSheetOpen}
        onOpenChange={setIsContactRequestSheetOpen}
        rejectedAssetIds={rejectedAssetIds}
      />
      <ScrollView
        className={cn(
          'flex-1 rounded-content transition-opacity duration-150',
          isInactive && 'pointer-events-none opacity-50'
        )}
        offsetTrackHeight
        contentContainerClassName="gap-3"
        footer={
          showRequestButton ? (
            <Animated.View className="self-center pb-6" style={footerAnimatedStyle}>
              {requestContactButton}
            </Animated.View>
          ) : undefined
        }>
        <ContentBox
          scrollable={false}
          contentContainerClassName="gap-6 px-6 py-6"
          header={<LeadHeader lead={lead} />}>
          <View className="gap-6">
            {lead.disabled_reason === 'RESERVED' && (
              <View className="flex-row items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
                <Icon name="lock-closed-outline" size={18} className="text-amber-600" />
                <Body className="flex-1 text-sm text-amber-700">
                  {t(($) => $.detail.status.reservedNotice)}
                </Body>
              </View>
            )}
            <View className="mx-auto w-full max-w-lg overflow-hidden rounded-xl border border-border">
              <ValueList values={teaserValues} valueClassName="blur-[3px] select-none" />
            </View>
            <LeadReadinessIntelligence readiness={lead.feedbackIntelligence?.readiness ?? null} />
          </View>
          <View
            className="gap-4"
            onLayout={(event) => {
              const { y, height } = event.nativeEvent.layout
              inlineButtonBottomY.value = y + height
            }}>
            {hasPendingRequest ? <ContactRequestPendingStatus /> : requestContactButton}
            {!hasFeedContent && !isLoadingFeedContent && (
              <LinkButton variant="link" size="default" href="/">
                {t(($) => $.common.notFound.goHome)}
              </LinkButton>
            )}
          </View>
        </ContentBox>
        <LeadTabsContent tabs={tabs} />
      </ScrollView>
    </Tabs>
  )
}
